/**
 * FeedPage Component - SSR-Safe Version
 *
 * Versão simplificada do FeedPage original, otimizada para SSR.
 * Características:
 * - Sem dependências de localStorage/sessionStorage fora de useEffect
 * - Guards obrigatórios antes de acessar APIs do navegador
 * - IntersectionObserver apenas no cliente
 * - Renderização básica funcional no servidor
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import QuestionCard from './QuestionCard'
import PaywallModal from './PaywallModal'

// ========================================
// TYPES
// ========================================

interface NormalizedAnswer {
  id: string
  text: string
  order_index: number
  explanation?: string | null
}

interface FeedItem {
  type: string
  content: {
    id: string
    text: string
    answers: NormalizedAnswer[]
    correct_answer_index: number
    difficulty: string
    category?: {
      name: string
      slug: string
      icon?: string
      color?: string
    }
    image_url?: string
  }
}

interface AnswerResult {
  questionId: string
  isCorrect: boolean
  correctIndex: number
  correctAnswerId?: string | null
  xpEarned: number
  coinsEarned: number
  selectedAnswerExplanation?: string | null
  correctAnswerExplanation?: string | null
}

interface FeedPageProps {
  initialItems?: FeedItem[]
  onAnswer?: (questionId: string, answerId: string, timeSpentMs?: number) => Promise<AnswerResult>
}

// ========================================
// COMPONENT
// ========================================

export default function FeedPage({ initialItems = [], onAnswer }: FeedPageProps) {
  // ========================================
  // STATE
  // ========================================

  const [items] = useState<FeedItem[]>(initialItems)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [results, setResults] = useState<Map<string, AnswerResult>>(new Map())
  const [isLoading, setIsLoading] = useState(false)

  // Session progress tracking (JRN-001)
  const [sessionCorrect, setSessionCorrect] = useState(0)
  const [sessionTotal, setSessionTotal] = useState(0)
  const sessionProgress = sessionTotal > 0
    ? Math.round((sessionCorrect / sessionTotal) * 100)
    : 0
  const [showPaywall, setShowPaywall] = useState(false)

  // SSR-SAFE: useRef é seguro no servidor
  const containerRef = useRef<HTMLDivElement>(null)

  // ========================================
  // HANDLERS
  // ========================================

  const handleAnswer = useCallback(
    async (questionId: string, answerId: string, timeSpentMs?: number) => {
      // Prevenir respostas duplicadas
      if (results.has(questionId)) return

      // Se não houver callback de resposta, apenas marcar como respondida localmente
      if (!onAnswer) {
        // Simulação básica para demonstração
        const mockResult: AnswerResult = {
          questionId,
          isCorrect: Math.random() > 0.5,
          correctIndex: 0,
          correctAnswerId: answerId,
          xpEarned: 10,
          coinsEarned: 0,
        }
        setSessionTotal(t => t + 1)
        if (mockResult.isCorrect) setSessionCorrect(c => c + 1)
        setResults(prev => new Map(prev).set(questionId, mockResult))
        return
      }

      setIsLoading(true)

      try {
        const result = await onAnswer(questionId, answerId, timeSpentMs)

        setSessionTotal(t => t + 1)
        if (result.isCorrect) setSessionCorrect(c => c + 1)

        setResults(prev => {
          const newMap = new Map(prev)
          newMap.set(questionId, result)
          return newMap
        })
      } catch (error) {
        console.error('[FeedPage] Erro ao enviar resposta:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [onAnswer, results]
  )

  const getResult = useCallback((id: string) => results.get(id), [results])

  // ========================================
  // EFFECTS
  // ========================================

  // Paywall trigger: 75% acertos com >= 5 questões respondidas
  useEffect(() => {
    if (sessionProgress >= 75 && sessionTotal >= 5 && !showPaywall) {
      setShowPaywall(true)
    }
  }, [sessionProgress, sessionTotal, showPaywall])

  // SSR-SAFE: IntersectionObserver apenas no cliente
  useEffect(() => {
    // Guard: apenas no cliente
    if (typeof window === 'undefined') return
    if (typeof IntersectionObserver === 'undefined') return

    const container = containerRef.current
    if (!container || items.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'))
            if (!isNaN(index)) {
              setCurrentIndex(index)
            }
          }
        })
      },
      { root: container, threshold: 0.6 }
    )

    const cards = container.querySelectorAll('.feed-card')
    cards.forEach((card) => observer.observe(card))

    return () => observer.disconnect()
  }, [items])

  // ========================================
  // RENDER
  // ========================================

  return (
    <div className="min-h-screen bg-black">
      {/* Main Feed Container */}
      <div className="flex-1 h-screen flex flex-col overflow-hidden">
        {/* Header */}
        <header className="relative flex flex-col px-6 py-4 border-b border-white/5 bg-black/90 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold text-white">CNH PASS</h1>
          </div>
          {sessionTotal > 0 && (
            <div className="w-full bg-white/10 h-1.5 rounded-full mt-2">
              <div
                className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${sessionProgress}%` }}
              />
            </div>
          )}
          {sessionTotal > 0 && (
            <span className="text-xs text-white/40 mt-1">
              {sessionProgress}% pronto para prova
            </span>
          )}
        </header>

        {/* Feed Container */}
        <main className="flex-1 flex justify-center overflow-hidden">
          <div
            ref={containerRef}
            className="w-full max-w-[500px] h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
            style={{
              scrollBehavior: 'smooth',
            }}
          >
            {/* Loading State */}
            {isLoading && items.length === 0 && (
              <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-2 border-white/20 border-t-green-500 rounded-full animate-spin" />
                  <span className="text-white/50 text-sm">Carregando questões...</span>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && items.length === 0 && (
              <div className="h-full flex items-center justify-center px-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">📚</span>
                  </div>
                  <p className="text-white/50 mb-2">Nenhuma questão disponível</p>
                  <p className="text-white/30 text-sm">Tente novamente mais tarde</p>
                </div>
              </div>
            )}

            {/* Question Cards */}
            {items.map((item, index) => (
              <div
                key={item.content.id}
                data-index={index}
                className="feed-card h-full w-full snap-start snap-always"
              >
                {item.type === 'question' ? (
                  <QuestionCard
                    question={item.content}
                    result={getResult(item.content.id)}
                    onAnswer={(answerId, timeSpentMs) =>
                      handleAnswer(item.content.id, answerId, timeSpentMs)
                    }
                    isActive={index === currentIndex}
                    questionNumber={index + 1}
                    totalQuestions={items.length}
                  />
                ) : null}
              </div>
            ))}

            {/* Session Complete Card */}
            {items.length > 0 && currentIndex >= items.length - 1 && (
              <div className="h-full snap-start flex items-center justify-center px-8">
                <div className="text-center">
                  <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30">
                    <span className="text-5xl">🎉</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Sessão Completa!</h3>
                  <p className="text-white/50 mb-4">Você respondeu {results.size} questões</p>
                  {results.size > 0 && (
                    <div className="flex justify-center gap-6 text-sm">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-500">
                          {Array.from(results.values()).filter(r => r.isCorrect).length}
                        </div>
                        <div className="text-white/40">Acertos</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white">
                          {Math.round(
                            (Array.from(results.values()).filter(r => r.isCorrect).length /
                              results.size) *
                              100
                          )}
                          %
                        </div>
                        <div className="text-white/40">Precisão</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Paywall Modal */}
      {showPaywall && (
        <PaywallModal
          onClose={() => setShowPaywall(false)}
          onSuccess={() => {
            setShowPaywall(false)
            // Refresh premium status - reload page to get updated feed
            if (typeof window !== 'undefined') {
              window.location.reload()
            }
          }}
        />
      )}
    </div>
  )
}
